import React from 'react';
import { Link } from 'react-router-dom';
import classes from './Blog.module.css';
import Seo, { SITE_URL } from './components/Seo';
import Header from './components/Header';
import Footer from './components/Footer';
import { useI18n } from './i18n/LanguageContext';
import { localePath } from './i18n/config';
import { useContent } from './content';
import { slugFor } from './content/slugs';

function Blog() {
  const { t, path, locale, formatDate } = useI18n();
  const { posts } = useContent();

  const blogUrl = `${SITE_URL}${localePath(locale, '/blog')}`;
  const postUrl = (id) => `${SITE_URL}${localePath(locale, `/blog/${slugFor(id, locale)}`)}`;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      '@id': `${blogUrl}#blog`,
      name: t('blog.schemaName'),
      description: t('blog.schemaDescription'),
      url: blogUrl,
      inLanguage: locale,
      publisher: { '@type': 'Organization', name: 'Portal Studio', url: SITE_URL },
      blogPost: posts.map((p) => ({
        '@type': 'BlogPosting',
        headline: p.title,
        url: postUrl(p.id),
        datePublished: p.datePublished,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: t('post.home'),
          item: `${SITE_URL}${localePath(locale, '/')}`,
        },
        { '@type': 'ListItem', position: 2, name: t('post.blog'), item: blogUrl },
      ],
    },
  ];

  const [featured, ...rest] = posts;

  return (
    <div className={classes.page}>
      <Seo
        title={t('meta.blog.title')}
        description={t('meta.blog.description')}
        routePath="/blog"
        jsonLd={jsonLd}
      />
      <Header />

      <main className={classes.main}>
        <div className={classes.hero}>
          <h1 className={classes.title}>
            {t('blog.titlePre')}
            <span className={classes.gradientText}>{t('blog.titleAccent')}</span>
            {t('blog.titlePost')}
          </h1>
          <p className={classes.subtitle}>{t('blog.subtitle')}</p>
        </div>

        <Link to={path(`/blog/${slugFor(featured.id, locale)}`)} className={classes.featured}>
          <span className={classes.category}>{featured.category}</span>
          <h2 className={classes.featuredTitle}>{featured.title}</h2>
          <p className={classes.featuredExcerpt}>{featured.excerpt}</p>
          <p className={classes.cardMeta}>
            <time dateTime={featured.datePublished}>{formatDate(featured.datePublished)}</time>
            <span aria-hidden="true"> · </span>
            {t('blog.readTime', { n: featured.readTime })}
          </p>
        </Link>

        <div className={classes.grid}>
          {rest.map((post) => (
            <Link
              key={post.id}
              to={path(`/blog/${slugFor(post.id, locale)}`)}
              className={classes.card}
            >
              <span className={classes.category}>{post.category}</span>
              <h2 className={classes.cardTitle}>{post.title}</h2>
              <p className={classes.cardExcerpt}>{post.excerpt}</p>
              <p className={classes.cardMeta}>
                <time dateTime={post.datePublished}>{formatDate(post.datePublished)}</time>
                <span aria-hidden="true"> · </span>
                {t('blog.readTime', { n: post.readTime })}
              </p>
            </Link>
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default Blog;
